// Copyright 2021-2022 zcloak authors & contributors
// SPDX-License-Identifier: Apache-2.0

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { alpha, Box, Button, Stack, styled, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import ButtonEnable from '../../components/ButtonEnable';
import { usePhase } from '../../hooks/usePhase';
import IconFalse from './icon_false.webp';
import IconLock from './icon_lock.webp';
import IconTrue from './icon_true.webp';

const Content = styled(Stack)({
  width: '25%'
});

const Progress = styled(Box)({
  position: 'relative',
  width: '75%',
  height: 6,
  margin: '24px 0',
  background: alpha('#aaa', 0.67),
  '> .Steps_Bg': {
    height: '100%',
    background: 'linear-gradient(90deg, #2B66FF 0%, #2F84EB 69%, #5FFF78 100%)'
  },
  '> .Steps_Point': {
    position: 'absolute',
    top: -4,
    width: 16,
    height: 16,
    borderRadius: 8,
    background: '#D1D1D1'
  }
});

const config = [
  {
    phase: 'Phase I',
    title: (
      <>
        Guess
        <br />
        and Record
      </>
    ),
    time: 'Dec.1 - Dec.9',
    startTime: 1669824000000,
    endTime: 1670515200000
  },
  {
    phase: 'Phase II',
    title: (
      <>
        Zero-Knowledge
        <br />
        Proof for Semi-Finals
      </>
    ),
    time: 'Dec.12 - Dec.16',
    startTime: 1670806800000,
    endTime: 1671235200000
  },
  {
    phase: 'Phase III',
    title: (
      <>
        Who is
        <br />
        the champion?
      </>
    ),
    time: 'Dec.18 - Dec.20',
    startTime: 1671292800000,
    endTime: 1671580799000
  },
  {
    phase: 'Phase IV',
    title: (
      <>
        Claim
        <br />
        your prizes
      </>
    ),
    time: 'Dec.21 - Future',
    startTime: 1671552000000,
    endTime: 1672416000000
  }
];

function Status({ message, status }: { status: 0 | 1 | 2; message?: string }) {
  return (
    <Stack alignItems="center" direction="row" mb={2} spacing={0.6}>
      <img src={status === 0 ? IconTrue : status === 1 ? IconFalse : IconLock} />
      <Typography>
        {status === 0 ? 'Successfully' : status === 1 ? message ?? 'Waiting for operation' : ''}
      </Typography>
    </Stack>
  );
}

function Action({ onClick, text }: { text: string; onClick?: () => void }) {
  return (
    <Stack spacing={1.5} width={200}>
      <Button
        endIcon={<ArrowRightAltIcon />}
        onClick={() =>
          window.open(
            'https://zcloaknetwork.medium.com/phase-iv-worlds-first-zk-guessing-for-the-world-cup-games-claim-your-prizes-4defd293dadd'
          )
        }
        sx={{
          background: '#B4C3C4',
          ':hover': {
            background: '#B4C3C4'
          }
        }}
      >
        View Guide
      </Button>
      <ButtonEnable endIcon={<ArrowRightAltIcon />} onClick={onClick} variant="contained">
        {text}
      </ButtonEnable>
    </Stack>
  );
}

const afterStyle = {
  content: '""',
  display: 'block',
  position: 'absolute',
  left: -7,
  top: -7,
  width: 30,
  height: 30,
  borderRadius: 15,
  background: '#0049FF',
  opacity: 0.42
};

function Steps({ current }: { current: number }) {
  const phases = usePhase();
  const percent = useMemo(() => {
    const base = (current / 3) * 100;

    const currentPercent = Math.max(
      0,
      Math.min(
        100 / 3,
        ((Date.now() - config[current].startTime) /
          (config[current].endTime - config[current].startTime)) *
          100
      )
    );

    return base + currentPercent;
  }, [current]);
  const navigate = useNavigate();

  return (
    <Box>
      <Stack direction="row">
        {config.map((c, index) => (
          <Content key={index}>
            <Typography fontWeight={400} variant="h5">
              {c.phase}
            </Typography>
            <Typography fontWeight={700} variant="h5">
              {c.title}
            </Typography>
            <Typography fontWeight={400} variant="h6">
              {c.time}
            </Typography>
          </Content>
        ))}
      </Stack>
      <Progress>
        <Box
          className="Steps_Bg"
          sx={{
            width: `${percent}%`
          }}
        />
        <Box
          className="Steps_Point"
          sx={() => ({
            background: current > -1 ? '#3671FF !important' : undefined,
            left: 'calc(0% - 4px)',
            ':after': current === 0 ? afterStyle : undefined
          })}
        />
        <Box
          className="Steps_Point"
          sx={() => ({
            background: current > 0 ? '#3671FF !important' : undefined,
            left: '33.33%',
            ':after': current === 1 ? afterStyle : undefined
          })}
        />
        <Box
          className="Steps_Point"
          sx={() => ({
            background: current > 1 ? '#3671FF !important' : undefined,
            left: '66.66%',
            ':after': current === 2 ? afterStyle : undefined
          })}
        />
        <Box
          className="Steps_Point"
          sx={() => ({
            background: current > 2 ? '#3671FF !important' : undefined,
            left: '100%',
            ':after': current === 3 ? afterStyle : undefined
          })}
        />
      </Progress>
      <Stack
        direction="row"
        sx={{
          '& > .Steps_Result': {
            width: '25%'
          }
        }}
      >
        <Box className="Steps_Result">
          {phases && <Status status={phases[0] ? 0 : 1} />}
          <Action
            onClick={() => window.open('https://cred.zkid.app/#/claimer/claims')}
            text="View my VC"
          />
        </Box>
        <Box className="Steps_Result">
          {phases && <Status status={phases[1] ? 0 : 1} />}
          <Action onClick={() => navigate('/verify/zk')} text="Go and Verify" />
        </Box>
        <Box className="Steps_Result">
          <>
            {phases && <Status status={phases[2] ? 0 : 1} />}
            <Action onClick={() => navigate('/verify/champion')} text="Go and Verify" />
          </>
        </Box>
        <Box className="Steps_Result">
          <>
            {phases && <Status status={phases[3] ? 0 : 1} />}
            <Action onClick={() => navigate('/prize')} text="Go and Claim" />
          </>
        </Box>
      </Stack>
    </Box>
  );
}

export default Steps;
